import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { generateDescription } from '../../../util/description';
import { generateFundraiserUrl } from '../../../util/fundraiser';
import { BeneficialOwnerData } from '../../BeneficialOwners';
import generateBeneficialOwners from './beneficialOwnersVariables';
import { VAULT_CREATE_KYB_KYC_SUBMIT_QUERY } from './graphqlQuery';
import { StripeData } from './types';

export const requestBodyFormatter =
  ({
    org,
    payableOrg,
    financialAccountRoots,
    kybBusinessStructure,
    kybBusinessType,
    kybAddressRegion,
    representativeIsOwner,
    representativeAddressRegion,
    representativeTitle,
    representativePercentOwnership,
    beneficialOwnersData,
    userKyc,
    fundraiserId,
    stripeData,
    gateway,
    representativeUserId,
    payableOrgId,
    fundraiserSlug,
  }: {
    org?: OrgNode;
    payableOrg?: OrgNode;
    financialAccountRoots: FinancialAccountNode[];
    kybBusinessStructure: string;
    kybBusinessType: string;
    kybAddressRegion: string;
    representativeIsOwner: boolean;
    representativeAddressRegion: string;
    representativeTitle: string;
    representativePercentOwnership: number;
    beneficialOwnersData: BeneficialOwnerData[];
    userKyc?: Kyc;
    fundraiserId?: string | null;
    stripeData?: StripeData;
    gateway: VaultProvider;
    representativeUserId?: string;
    payableOrgId?: string;
    fundraiserSlug?: string;
  }) =>
  (formValues: Record<string, string>): VgsRequestBody => {
    const financialAccountAncestor =
      payableOrg !== undefined
        ? // we search the financialAccountRoots in reverse order because
          // we want to find the highest possible org ein_number match
          financialAccountRoots
            .reverse()
            .find((fan) => fan.orgEin === payableOrg.fields.ein_number)
        : undefined;
    const filteredBeneficialOwners = beneficialOwnersData.filter(
      (bod) =>
        bod.addressRegion !== undefined && bod.percentOwnership !== undefined,
    ) as Required<BeneficialOwnerData>[];

    const rawRepresentativeData: {
      userId?: string;
      kycId?: string;
      kycData?: Kyc;
    } =
      userKyc?.id !== undefined
        ? {
            kycId: userKyc.id,
          }
        : {
            kycData: {
              firstName: formValues.representativeKycFirstName,
              lastName: formValues.representativeKycLastName,
              phoneNumber: formValues.representativeKycPhoneNumber,
              email: formValues.representativeKycEmail,
              dob: {
                month: formValues.representativeKycDobMonth,
                day: formValues.representativeKycDobDay,
                year: formValues.representativeKycDobYear,
              },
              address: {
                street: formValues.representativeKycAddressStreet,
                unit: formValues.representativeKycAddressUnit,
                city: formValues.representativeKycAddressCity,
                region: representativeAddressRegion,
                postalCode: formValues.representativeKycAddressPostalCode,
              },
              ssn: formValues.representativeKycSsn,
              director: false,
              executive: true,
              representative: true,
              owner: representativeIsOwner,
              percentOwnership: representativePercentOwnership,
              title: representativeTitle,
            },
          };
    rawRepresentativeData.userId = representativeUserId;

    return {
      query: VAULT_CREATE_KYB_KYC_SUBMIT_QUERY,
      variables: {
        rawOrgData: {
          orgId: org?.id,
          legalName:
            financialAccountAncestor?.orgName ||
            org?.name ||
            formValues.kybCustomerFacingName,
          customerFacingName:
            org?.fields.legal_name || org?.name || formValues.kybPayableName,
          type: kybBusinessType,
          structure: kybBusinessStructure,
          url: generateFundraiserUrl({ fundraiserSlug, fundraiserId }) || null,
          phoneNumber: org?.fields.phone || formValues.kybPhoneNumber,
          taxId: org?.fields.ein_number || formValues.kybTaxId,
          address: {
            street: org?.fields.street || formValues.kybAddressStreet,
            unit: formValues.kybAddressUnit,
            city: org?.fields.city || formValues.kybAddressCity,
            region: org?.fields.state_code || kybAddressRegion,
            postalCode: org?.fields.zip_code || formValues.kybAddressPostalCode,
          },
          description: generateDescription({ org, formValues }),
        },
        rawRepresentativeData,
        stripeData,
        gateway,
        rawBeneficialOwners: generateBeneficialOwners(
          filteredBeneficialOwners,
          formValues,
        ),
        saveToPayableOrgId: payableOrgId,
      },
    };
  };
